import { Component } from '@angular/core';
import { map, tap } from 'rxjs/operators';

import { ForceSyncService } from './sync.force';
import { BaseSyncButtonComponent } from './sync-button-base.component';

/**
 * Sync button for all forces.
 * Displays global sync status and triggers sync for all forces.
 *
 * Usage:
 * ```html
 * <abs-sync-all-forces-button></abs-sync-all-forces-button>
 * ```
 */
@Component({
    standalone: false,
    selector: 'abs-sync-all-forces-button',
    template: `
        @if (style === 'listItem') {
            <ion-item [class]="syncStatus" (click)="onSyncClick()" [button]="true" [disabled]="syncStatus === 'InProgress'">
                <abs-sync-status-icon [status]="syncStatus" [showClean]="true" [color]="iconColor"> </abs-sync-status-icon>
                {{ label }}
            </ion-item>
        } @else if (style === 'button') {
            <ion-button (click)="onSyncClick()" [disabled]="syncStatus === 'InProgress'" title="{{ label }}">
                <abs-sync-status-icon [status]="syncStatus" [showClean]="true" [color]="iconColor"> </abs-sync-status-icon>
            </ion-button>
        }
    `,
    styles: [
        `
            :host {
                display: block;
            }

            ion-item {
                --background: white;
                --inner-padding-end: 0;
            }

            ion-item abs-sync-status-icon {
                margin-right: var(--gutter);
            }

            ion-item.Error {
                color: var(--ion-color-danger, #eb445a);
            }

            ion-item.InProgress {
                color: var(--ion-color-primary, #3880ff);
            }
        `
    ]
})
export class SyncAllForcesButtonComponent extends BaseSyncButtonComponent {
    constructor(private forceSyncService: ForceSyncService) {
        super();
    }

    protected setupSubscription(): void {
        // Subscribe to the global sync status from ForceSyncService
        this.subscription = this.forceSyncService.syncStatus$
            .pipe(
                map((status) => this.mapGlobalStatusToSyncStatus(status.status)),
                tap((status) => (this.syncStatus = status))
            )
            .subscribe();
    }

    onSyncClick(): void {
        if (this.syncStatus !== 'InProgress') {
            // Manual sync - always show visual feedback
            this.forceSyncService.sync({ manual: true });
        }
    }

    get label(): string {
        return this.syncStatus === 'Clean' ? 'All synced' : super.label;
    }
}
